import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastProvider, AnimatedPage } from '@theonefamily/ui';
import { BrandingProvider } from './contexts/BrandingContext';
import { useAuth } from './hooks/useAuth';
import { useEntitlements } from './hooks/useEntitlements';
import { AppLayout } from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SSOCallbackPage from './pages/SSOCallbackPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import SupportPage from './pages/SupportPage';
import ErrorCaptureWrapper from './error-capture/ErrorCaptureWrapper';

// SecOps pages (SOC product)
import SecurityCommandCenterPage from './pages/secops/SecurityCommandCenterPage';
import AlertsPage from './pages/secops/AlertsPage';
import AlertDetailPage from './pages/secops/AlertDetailPage';
import IncidentsPage from './pages/secops/IncidentsPage';
import IncidentDetailPage from './pages/secops/IncidentDetailPage';
import PlaybooksPage from './pages/secops/PlaybooksPage';
import PlaybookDetailPage from './pages/secops/PlaybookDetailPage';
import ThreatHuntingPage from './pages/secops/ThreatHuntingPage';
import ThreatHuntDetailPage from './pages/secops/ThreatHuntDetailPage';
import ResponseActionsPage from './pages/secops/ResponseActionsPage';
import LogSourcesPage from './pages/secops/LogSourcesPage';
import ForensicsPage from './pages/secops/ForensicsPage';
import InvestigationDetailPage from './pages/secops/InvestigationDetailPage';
import IocSearchPage from './pages/secops/IocSearchPage';
import SecurityAnalyticsPage from './pages/secops/SecurityAnalyticsPage';
import DetectionRulesPage from './pages/secops/DetectionRulesPage';
import ThreatModelingPage from './pages/secops/ThreatModelingPage';
import ThreatIntelPage from './pages/secops/ThreatIntelPage';
import EscalationPage from './pages/secops/EscalationPage';
import CorrelationDashboardPage from './pages/secops/CorrelationDashboardPage';
import CrossClientThreatsPage from './pages/secops/CrossClientThreatsPage';
import HuntMarketplacePage from './pages/secops/HuntMarketplacePage';
import PlaybookBuilderPage from './pages/secops/PlaybookBuilderPage';
import UEBAPage from './pages/secops/UEBAPage';
import InvestigationWorkbenchPage from './pages/secops/InvestigationWorkbenchPage';
import SOCAutopilotPage from './pages/secops/SOCAutopilotPage';
import AttackSimulationPage from './pages/secops/AttackSimulationPage';
import DeceptionPage from './pages/secops/DeceptionPage';
import LogAnalysisPage from './pages/secops/LogAnalysisPage';

// Shadow AI + SaaS Posture moved to The One Protect — pages removed from Security

// NOTE: Dark web, SAT, and email-security pages moved to The One Protect (app.theoneprotect.app)
// NOTE: AppSec pages (VulnerabilitiesPage, FindingsPage, ScansPage, DependenciesPage,
//   PentestsPage, PosturePage) moved to The One Code (SESSION-APPSEC-TO-CODE ✅)
// NOTE: VulnMgmt pages (NetworkScansPage, PatchManagementPage, RemediationPage,
//   VulnExceptionsPage, AITriagePage) moved to The One RMM (SESSION-VULN-PATCH-TO-RMM)

// Compliance pages removed — moved to The One Compliance (app.theonecompliance.app)

const HUB_BILLING_URL = (import.meta.env.VITE_HUB_URL ?? 'https://hub.theonestack.com') + '/billing';

function UpgradeGate({ product, label }: { product: string; label: string }) {
  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="max-w-md w-full mx-4 rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-indigo-500/10">
          <svg className="h-7 w-7 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 12c0 6.627 5.373 12 12 12s12-5.373 12-12c0-2.021-.504-3.926-1.389-5.595" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-white mb-2">Upgrade to {label}</h2>
        <p className="text-white/50 text-sm mb-6">
          Activate {label} from your Hub billing page to access this section.
        </p>
        <a
          href={HUB_BILLING_URL}
          className="inline-block rounded-xl bg-indigo-600 hover:bg-indigo-500 px-6 py-2.5 text-sm font-medium text-white transition-colors"
        >
          Activate in Hub Billing
        </a>
      </div>
    </div>
  );
}

function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { user, loading, tenants, currentTenantId } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" style={{ borderColor: 'var(--accent)' }} /></div>;
  if (!user) return <Navigate to="/login" />;
  const tier = tenants.find(t => t.id === currentTenantId)?.tier ?? tenants[0]?.tier ?? 'free';
  if (tier === 'free' && location.pathname !== '/billing') {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0a0a0a]">
        <div className="max-w-md w-full mx-4 rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10">
            <svg className="h-7 w-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 12c0 6.627 5.373 12 12 12s12-5.373 12-12c0-2.021-.504-3.926-1.389-5.595" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Subscription Required</h2>
          <p className="text-white/50 text-sm mb-6">
            Activate The One Security from your Hub billing page to access the platform.
          </p>
          <a href={HUB_BILLING_URL} className="inline-block rounded-xl bg-red-600 hover:bg-red-500 px-6 py-2.5 text-sm font-medium text-white transition-colors">
            Activate in Hub Billing
          </a>
        </div>
      </div>
    );
  }
  return <AppLayout>{children}</AppLayout>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return <SubscriptionGate>{children}</SubscriptionGate>;
}

function SOCRoute({ children }: { children: React.ReactNode }) {
  const { entitlements, loading } = useEntitlements();
  if (loading) return null;
  if (!entitlements.soc_enabled) return <UpgradeGate product="soc" label="The One SOC" />;
  return <>{children}</>;
}

// ComplianceRoute removed — compliance moved to The One Compliance

// ProtectRoute removed — all Protect features moved to The One Protect

// Inline component for moved Protect features
const ProtectMovedRedirect: React.FC = () => (
  <div style={{ padding: '48px 24px', maxWidth: 560, margin: '0 auto', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
    <div style={{ fontSize: 48, marginBottom: 16 }}>🛡️</div>
    <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1e1b4b', marginBottom: 12 }}>
      Feature Moved to The One Protect
    </h2>
    <p style={{ color: '#64748b', marginBottom: 24, lineHeight: 1.6 }}>
      Dark web monitoring, DMARC, security awareness training, and phishing simulation have moved to <strong>The One Protect</strong> — a dedicated product for user-layer security.
    </p>
    <a
      href="https://app.theoneprotect.app"
      style={{
        display: 'inline-block',
        background: '#8b5cf6',
        color: '#ffffff',
        padding: '12px 28px',
        borderRadius: 8,
        textDecoration: 'none',
        fontWeight: 700,
        fontSize: 15,
      }}
    >
      Launch The One Protect →
    </a>
  </div>
);

export default function App() {
  return (
    <BrandingProvider>
    <ToastProvider>
    <ErrorCaptureWrapper>
    <BrowserRouter basename="/security">
      <AnimatedPage>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/sso" element={<SSOCallbackPage />} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

        {/* Always-accessible routes */}
        <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />

        {/* SOC routes */}
        <Route path="/secops" element={<ProtectedRoute><SOCRoute><SecurityCommandCenterPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/alerts" element={<ProtectedRoute><SOCRoute><AlertsPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/alerts/:id" element={<ProtectedRoute><SOCRoute><AlertDetailPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/incidents" element={<ProtectedRoute><SOCRoute><IncidentsPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/incidents/:id" element={<ProtectedRoute><SOCRoute><IncidentDetailPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/playbooks" element={<ProtectedRoute><SOCRoute><PlaybooksPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/playbooks/:id" element={<ProtectedRoute><SOCRoute><PlaybookDetailPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/threat-hunting" element={<ProtectedRoute><SOCRoute><ThreatHuntingPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/threat-hunting/:id" element={<ProtectedRoute><SOCRoute><ThreatHuntDetailPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/response-actions" element={<ProtectedRoute><SOCRoute><ResponseActionsPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/log-sources" element={<ProtectedRoute><SOCRoute><LogSourcesPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/forensics" element={<ProtectedRoute><SOCRoute><ForensicsPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/forensics/:id" element={<ProtectedRoute><SOCRoute><InvestigationDetailPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/ioc-search" element={<ProtectedRoute><SOCRoute><IocSearchPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/analytics" element={<ProtectedRoute><SOCRoute><SecurityAnalyticsPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/detection-rules" element={<ProtectedRoute><SOCRoute><DetectionRulesPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/threat-intel" element={<ProtectedRoute><SOCRoute><ThreatIntelPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/escalation" element={<ProtectedRoute><SOCRoute><EscalationPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/correlations" element={<ProtectedRoute><SOCRoute><CorrelationDashboardPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/threat-model" element={<ProtectedRoute><SOCRoute><ThreatModelingPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/cross-client-threats" element={<ProtectedRoute><SOCRoute><CrossClientThreatsPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/hunt-marketplace" element={<ProtectedRoute><SOCRoute><HuntMarketplacePage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/playbook-builder" element={<ProtectedRoute><SOCRoute><PlaybookBuilderPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/ueba" element={<ProtectedRoute><SOCRoute><UEBAPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/investigation-workbench" element={<ProtectedRoute><SOCRoute><InvestigationWorkbenchPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/autopilot" element={<ProtectedRoute><SOCRoute><SOCAutopilotPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/attack-sim" element={<ProtectedRoute><SOCRoute><AttackSimulationPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/deception" element={<ProtectedRoute><SOCRoute><DeceptionPage /></SOCRoute></ProtectedRoute>} />
        <Route path="/secops/log-analysis" element={<ProtectedRoute><SOCRoute><LogAnalysisPage /></SOCRoute></ProtectedRoute>} />

        {/* All Protect features moved to The One Protect */}
        <Route path="/secops/dark-web" element={<ProtectedRoute><ProtectMovedRedirect /></ProtectedRoute>} />
        <Route path="/secops/shadow-ai" element={<ProtectedRoute><ProtectMovedRedirect /></ProtectedRoute>} />
        <Route path="/secops/saas-posture" element={<ProtectedRoute><ProtectMovedRedirect /></ProtectedRoute>} />
        <Route path="/sat/training" element={<ProtectedRoute><ProtectMovedRedirect /></ProtectedRoute>} />
        <Route path="/sat/phishing" element={<ProtectedRoute><ProtectMovedRedirect /></ProtectedRoute>} />
        <Route path="/sat/leaderboard" element={<ProtectedRoute><ProtectMovedRedirect /></ProtectedRoute>} />
        <Route path="/email-security/domains" element={<ProtectedRoute><ProtectMovedRedirect /></ProtectedRoute>} />
        <Route path="/email-security/alerts" element={<ProtectedRoute><ProtectMovedRedirect /></ProtectedRoute>} />
        <Route path="/email-security/dmarc" element={<ProtectedRoute><ProtectMovedRedirect /></ProtectedRoute>} />

        {/* Compliance moved to The One Compliance */}
        <Route path="/grc" element={
          <ProtectedRoute>
            <div className="flex items-center justify-center h-[60vh]">
              <div className="max-w-md w-full mx-4 rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
                <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-indigo-500/10">
                  <svg className="h-7 w-7 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">Compliance Has Moved</h2>
                <p className="text-white/50 text-sm mb-6">
                  GRC &amp; Compliance is now its own product: The One Compliance.
                </p>
                <a
                  href="https://app.theonecompliance.app"
                  className="inline-block rounded-xl bg-indigo-600 hover:bg-indigo-500 px-6 py-2.5 text-sm font-medium text-white transition-colors"
                >
                  Go to The One Compliance
                </a>
              </div>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/grc/*" element={
          <ProtectedRoute>
            <div className="flex items-center justify-center h-[60vh]">
              <div className="max-w-md w-full mx-4 rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
                <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-indigo-500/10">
                  <svg className="h-7 w-7 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">Compliance Has Moved</h2>
                <p className="text-white/50 text-sm mb-6">
                  GRC &amp; Compliance is now its own product: The One Compliance.
                </p>
                <a
                  href="https://app.theonecompliance.app"
                  className="inline-block rounded-xl bg-indigo-600 hover:bg-indigo-500 px-6 py-2.5 text-sm font-medium text-white transition-colors"
                >
                  Go to The One Compliance
                </a>
              </div>
            </div>
          </ProtectedRoute>
        } />
      </Routes>
      </AnimatedPage>
    </BrowserRouter>
    </ErrorCaptureWrapper>
    </ToastProvider>
    </BrandingProvider>
  );
}
